/**
 * Profile Page
 * User account settings and profile management
 * For helpers: includes photo, service areas, bio, documents
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, helpersAPI, portsAPI, ratingsAPI, authAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  User,
  Mail,
  Phone,
  Save,
  Lock,
  Shield,
  Bell,
  Star,
  MapPin,
  Search,
  Plus,
  X,
  Upload,
  FileText,
  Trash2,
  File,
  Camera,
  Edit3,
  Globe,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
} from 'lucide-react';
import * as socketService from '../../services/socket';
// VISA/CERTS: Import visa and certificate components
import VisaSection from '../../components/VisaSection';
import CertificateSection from '../../components/CertificateSection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function ProfilePage() {
  const { user, updateUser } = useAuth();
  const { showToast } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp: '',
  });

  const isHelper = user?.user_type === 'helper';

  // Helper-specific state
  const [helperLoading, setHelperLoading] = useState(isHelper);
  const [helperSaving, setHelperSaving] = useState(false);
  const [helperFormData, setHelperFormData] = useState({
    bio: '',
    experience_years: 0,
    date_of_birth: '',
    nationality: '',
    department: '',
    position: '',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [portSearch, setPortSearch] = useState('');
  const [portResults, setPortResults] = useState([]);
  const [searchingPorts, setSearchingPorts] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [availableNow, setAvailableNow] = useState(true); // Default to available

  // Reviews state
  const [receivedReviews, setReceivedReviews] = useState([]);
  const [loadingReceivedReviews, setLoadingReceivedReviews] = useState(false);
  const [sentReviews, setSentReviews] = useState([]);
  const [loadingSentReviews, setLoadingSentReviews] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewData, setEditReviewData] = useState({ rating: 0, comment: '' });

  // Languages state
  const [languages, setLanguages] = useState([]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const availableLanguages = [
    'English', 'French', 'Spanish', 'Italian', 'German', 'Portuguese',
    'Greek', 'Turkish', 'Arabic', 'Russian', 'Mandarin', 'Japanese',
    'Dutch', 'Croatian', 'Swedish', 'Norwegian', 'Danish'
  ];

  const documentTypes = [
    { value: 'cv', label: 'CV / Resume' },
    { value: 'reference', label: 'Reference Letter' },
    { value: 'recommendation', label: 'Recommendation' },
  ];

  // VISA/CERTS: Position options for crew profile
  const DEPARTMENTS = [
    { value: 'deck', label: 'Deck' },
    { value: 'interior', label: 'Interior' },
    { value: 'galley', label: 'Galley' },
    { value: 'engineering', label: 'Engineering' },
  ];

  const POSITIONS = {
    deck: [
      { value: 'deck_greenie', label: 'Greenie (Green Deckhand)' },
      { value: 'deck_deckhand', label: 'Deckhand' },
      { value: 'deck_bosun', label: 'Bosun' },
      { value: 'deck_first_mate', label: 'First Mate / Chief Officer' },
      { value: 'deck_captain', label: 'Captain / Master' },
    ],
    interior: [
      { value: 'interior_junior_stew', label: 'Junior Stewardess' },
      { value: 'interior_stew', label: 'Stewardess / Steward' },
      { value: 'interior_2nd_stew', label: '2nd Stewardess' },
      { value: 'interior_chief_stew', label: 'Chief Stewardess' },
      { value: 'interior_purser', label: 'Purser' },
    ],
    galley: [
      { value: 'galley_junior_chef', label: 'Junior Chef / Cook' },
      { value: 'galley_sole_chef', label: 'Sole Chef / Chef' },
      { value: 'galley_head_chef', label: 'Head Chef' },
    ],
    engineering: [
      { value: 'eng_junior_engineer', label: 'Junior Engineer / Wiper' },
      { value: 'eng_2nd_engineer', label: '2nd Engineer / ETO' },
      { value: 'eng_chief_engineer', label: 'Chief Engineer' },
    ],
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        let phone = user.phone || '';
        let whatsapp = user.whatsapp || '';

        if (isHelper) {
          try {
            const helperData = await helpersAPI.getMyProfile();
            if (helperData.profile?.phone && !phone) {
              phone = helperData.profile.phone;
            }
            if (helperData.profile?.whatsapp) {
              whatsapp = helperData.profile.whatsapp;
            }
          } catch (e) {
            // Ignore
          }
        }

        setFormData({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          phone: phone,
          whatsapp: whatsapp,
        });
      }
    };
    loadProfile();
  }, [user]);

  // Load profile photo for all users
  useEffect(() => {
    if (user?.profile_photo_url) {
      setProfilePhoto(getFullPhotoUrl(user.profile_photo_url));
    }
  }, [user?.profile_photo_url]);

  // Load helper-specific data
  useEffect(() => {
    if (!isHelper) return;

    const loadHelperData = async () => {
      try {
        const [profileData, areasData, docsData] = await Promise.all([
          helpersAPI.getMyProfile(),
          helpersAPI.getServiceAreas(),
          helpersAPI.getDocuments(),
        ]);

        const rawPhotoUrl = profileData.profile?.profile_photo_url || null;
        if (rawPhotoUrl) setProfilePhoto(getFullPhotoUrl(rawPhotoUrl));
        setServiceAreas(areasData.service_areas || []);
        setDocuments(docsData.data || []);

        if (profileData.profile) {
          setHelperFormData({
            bio: profileData.profile.bio || '',
            experience_years: profileData.profile.experience_years || 0,
            date_of_birth: profileData.profile.date_of_birth || '',
            nationality: profileData.profile.nationality || '',
            department: profileData.profile.department || '',
            position: profileData.profile.position || '',
          });
          setLanguages(profileData.profile.languages || []);
          // Load availability status (default to true if not set)
          setAvailableNow(profileData.profile.available_now !== false);
        }
      } catch (error) {
        console.error('Failed to load helper data:', error);
      } finally {
        setHelperLoading(false);
      }
    };
    loadHelperData();
  }, [isHelper]);

  // Load reviews (received + sent)
  useEffect(() => {
    const loadReviews = async () => {
      setLoadingReceivedReviews(true);
      setLoadingSentReviews(true);
      try {
        const [receivedRes, sentRes] = await Promise.all([
          usersAPI.getMyReviews(),
          usersAPI.getMySentReviews(),
        ]);
        setReceivedReviews(receivedRes.data || []);
        setSentReviews(sentRes.data || []);
      } catch (error) {
        console.error('Failed to load reviews:', error);
      } finally {
        setLoadingReceivedReviews(false);
        setLoadingSentReviews(false);
      }
    };
    loadReviews();
  }, []);

  // Port search debounce
  useEffect(() => {
    if (!isHelper) return;

    const searchPorts = async () => {
      if (portSearch.length < 2) {
        setPortResults([]);
        return;
      }
      setSearchingPorts(true);
      try {
        const data = await portsAPI.search(portSearch);
        const addedPortIds = new Set(serviceAreas.map(a => a.port_id));
        setPortResults((data.ports || []).filter(p => !addedPortIds.has(p.id)));
      } catch (error) {
        console.error('Port search failed:', error);
      } finally {
        setSearchingPorts(false);
      }
    };

    const debounce = setTimeout(searchPorts, 300);
    return () => clearTimeout(debounce);
  }, [portSearch, serviceAreas, isHelper]);

  const getFullPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${UPLOADS_BASE_URL}${url}`;
  };

  // Basic profile handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await usersAPI.updateProfile(formData);
      const updatedUser = response.data || response.user || response;
      updateUser(updatedUser);
      showToast('Profile updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper profile handlers
  const handleSaveHelperProfile = async () => {
    setHelperSaving(true);
    try {
      await helpersAPI.updateProfile(helperFormData);
      showToast('Helper profile updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update helper profile', 'error');
    } finally {
      setHelperSaving(false);
    }
  };

  // Toggle availability status
  const handleToggleAvailability = async () => {
    const newStatus = !availableNow;
    try {
      await helpersAPI.toggleAvailability(newStatus);
      socketService.toggleAvailability(newStatus);
      setAvailableNow(newStatus);
      showToast(newStatus ? 'You are now available for work!' : 'You are now set to offline', 'success');
    } catch (error) {
      showToast('Failed to update availability', 'error');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Client-side file size validation (5MB max)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      showToast('File too large. Maximum size is 5MB.', 'error');
      e.target.value = '';
      return;
    }

    setUploadingPhoto(true);
    try {
      // Use correct API based on user type
      const result = user.user_type === 'helper'
        ? await helpersAPI.uploadProfilePhoto(file)
        : await usersAPI.uploadProfilePhoto(file);
      const photoUrl = result.url || result.profile_photo_url;
      const fullUrl = getFullPhotoUrl(photoUrl);
      setProfilePhoto(fullUrl);
      updateUser({ profile_photo_url: photoUrl });
      showToast('Profile photo updated', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to upload photo', 'error');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleAddPort = async (port) => {
    try {
      const newArea = await helpersAPI.addServiceArea(port.id, port.name);
      setServiceAreas(prev => [...prev, { id: newArea.id, port_id: port.id, port_name: port.name }]);
      setPortSearch('');
      setPortResults([]);
      showToast(`Added ${port.name} to service areas`, 'success');
    } catch (error) {
      showToast(error.message || 'Failed to add service area', 'error');
    }
  };

  const handleRemovePort = async (areaId) => {
    try {
      await helpersAPI.removeServiceArea(areaId);
      setServiceAreas(prev => prev.filter(a => a.id !== areaId));
      showToast('Service area removed', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to remove service area', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const docType = e.target.dataset.doctype || 'other';
    setUploading(true);
    try {
      const document = await helpersAPI.uploadDocument(file, docType, '');
      setDocuments(prev => [document, ...prev]);
      showToast('Document uploaded successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to upload document', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await helpersAPI.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      showToast('Document deleted', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to delete document', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Review handlers
  const handleEditReview = (review) => {
    setEditingReview(review);
    setEditReviewData({ rating: review.rating, comment: review.comment || '' });
  };

  const handleSaveReview = async () => {
    if (!editingReview) return;
    try {
      await ratingsAPI.update(editingReview.id, editReviewData);
      setSentReviews(prev => prev.map(r =>
        r.id === editingReview.id
          ? { ...r, rating: editReviewData.rating, comment: editReviewData.comment }
          : r
      ));
      setEditingReview(null);
      showToast('Review updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update review', 'error');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await ratingsAPI.delete(reviewId);
      setSentReviews(prev => prev.filter(r => r.id !== reviewId));
      showToast('Review deleted', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to delete review', 'error');
    }
  };

  // Language handlers
  const handleAddLanguage = async (lang) => {
    const updatedLanguages = [...languages, lang];
    setLanguages(updatedLanguages);
    setShowLanguageDropdown(false);
    try {
      await helpersAPI.updateProfile({ languages: updatedLanguages });
    } catch (error) {
      setLanguages(languages); // rollback
      showToast(error.message || 'Failed to add language', 'error');
    }
  };

  const handleRemoveLanguage = async (lang) => {
    const updatedLanguages = languages.filter(l => l !== lang);
    setLanguages(updatedLanguages);
    try {
      await helpersAPI.updateProfile({ languages: updatedLanguages });
    } catch (error) {
      setLanguages(languages); // rollback
      showToast(error.message || 'Failed to remove language', 'error');
    }
  };

  const getDocTypeLabel = (type) => {
    const docType = documentTypes.find(d => d.value === type);
    return docType ? docType.label : type;
  };

  // Password change handler
  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (passwordData.new.length < 6) {
      showToast('New password must be at least 6 characters', 'error');
      return;
    }
    setChangingPassword(true);
    try {
      await authAPI.changePassword(passwordData.current, passwordData.new);
      showToast('Password changed successfully', 'success');
      setShowPasswordModal(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      showToast(error.message || 'Failed to change password', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  // Show spinner while helper data loads
  if (isHelper && helperLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Edit My Profile
        </h1>
        <p className="text-navy-600 mt-1">
          Manage your account settings{isHelper ? ', skills, and documents' : ''}
        </p>
      </div>

      {/* Profile Photo */}
      <div className="card">
        <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Camera className="h-5 w-5 inline mr-2" />
          Profile Photo
        </h2>
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden border-4 border-primary-200">
            {profilePhoto ? (
              <img src={profilePhoto} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <User className="h-12 w-12 text-primary-400" />
            )}
          </div>
          <div>
            <label className="btn-secondary cursor-pointer inline-flex items-center">
              <input
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={uploadingPhoto}
              />
              {uploadingPhoto ? (
                <>
                  <span className="spinner h-4 w-4 mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Photo
                </>
              )}
            </label>
            <p className="text-sm text-navy-500 mt-2">JPG, PNG. Max 5MB.</p>
          </div>
        </div>
      </div>

      {/* Basic Profile Info */}
      <div className="card">
        <div className="flex items-center mb-6">
          <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
            {user?.profile_photo_url ? (
              <img
                src={user.profile_photo_url.startsWith('http') ? user.profile_photo_url : `${UPLOADS_BASE_URL}${user.profile_photo_url}`}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-primary-700 font-bold text-xl">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            )}
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-semibold text-navy-900">
              {user?.first_name} {user?.last_name}
            </h2>
            <p className="text-navy-500 capitalize">
              {user?.user_type?.replace('_', ' ')}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="input-field"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="input-field"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Phone className="h-4 w-4 inline mr-1" />
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              placeholder="+1 234 567 8900"
            />
            <label className="flex items-center mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.whatsapp}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    whatsapp: e.target.checked ? prev.phone : ''
                  }));
                }}
                className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
              />
              <span className="ml-2 text-sm text-navy-600 flex items-center">
                <svg className="h-4 w-4 mr-1 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
                I'm on WhatsApp
              </span>
            </label>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Saving...
                </span>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Helper: About You / CV */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <User className="h-5 w-5 inline mr-2" />
            About You (CV)
          </h2>
          <p className="text-navy-600 mb-4">
            Write a brief summary of your experience and qualifications.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Years of Experience
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={helperFormData.experience_years}
                onChange={(e) => setHelperFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                className="input-field"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={helperFormData.date_of_birth}
                onChange={(e) => setHelperFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                className="input-field"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Nationality (Passport)
              </label>
              <select
                value={helperFormData.nationality}
                onChange={(e) => setHelperFormData(prev => ({ ...prev, nationality: e.target.value }))}
                className="input-field"
              >
                <option value="">Select...</option>
                <option value="AF">Afghan</option>
                <option value="AL">Albanian</option>
                <option value="DZ">Algerian</option>
                <option value="AR">Argentine</option>
                <option value="AU">Australian</option>
                <option value="AT">Austrian</option>
                <option value="BE">Belgian</option>
                <option value="BR">Brazilian</option>
                <option value="BG">Bulgarian</option>
                <option value="CA">Canadian</option>
                <option value="CL">Chilean</option>
                <option value="CN">Chinese</option>
                <option value="CO">Colombian</option>
                <option value="HR">Croatian</option>
                <option value="CY">Cypriot</option>
                <option value="CZ">Czech</option>
                <option value="DK">Danish</option>
                <option value="NL">Dutch</option>
                <option value="EG">Egyptian</option>
                <option value="EE">Estonian</option>
                <option value="FI">Finnish</option>
                <option value="FR">French</option>
                <option value="DE">German</option>
                <option value="GR">Greek</option>
                <option value="HU">Hungarian</option>
                <option value="IN">Indian</option>
                <option value="ID">Indonesian</option>
                <option value="IE">Irish</option>
                <option value="IL">Israeli</option>
                <option value="IT">Italian</option>
                <option value="JP">Japanese</option>
                <option value="LV">Latvian</option>
                <option value="LT">Lithuanian</option>
                <option value="MY">Malaysian</option>
                <option value="MT">Maltese</option>
                <option value="MX">Mexican</option>
                <option value="MC">Monegasque</option>
                <option value="ME">Montenegrin</option>
                <option value="MA">Moroccan</option>
                <option value="NZ">New Zealander</option>
                <option value="NO">Norwegian</option>
                <option value="PH">Filipino</option>
                <option value="PL">Polish</option>
                <option value="PT">Portuguese</option>
                <option value="RO">Romanian</option>
                <option value="RU">Russian</option>
                <option value="SA">Saudi</option>
                <option value="RS">Serbian</option>
                <option value="SG">Singaporean</option>
                <option value="SK">Slovak</option>
                <option value="SI">Slovenian</option>
                <option value="ZA">South African</option>
                <option value="KR">South Korean</option>
                <option value="ES">Spanish</option>
                <option value="SE">Swedish</option>
                <option value="CH">Swiss</option>
                <option value="TH">Thai</option>
                <option value="TR">Turkish</option>
                <option value="UA">Ukrainian</option>
                <option value="AE">Emirati</option>
                <option value="GB">British</option>
                <option value="US">American</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>

          {/* VISA/CERTS: Position Looking For */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Department
              </label>
              <select
                value={helperFormData.department}
                onChange={(e) => setHelperFormData(prev => ({
                  ...prev,
                  department: e.target.value,
                  position: '' // Reset position when department changes
                }))}
                className="input-field"
              >
                <option value="">Select department...</option>
                {DEPARTMENTS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Position Looking For
              </label>
              <select
                value={helperFormData.position}
                onChange={(e) => setHelperFormData(prev => ({ ...prev, position: e.target.value }))}
                className="input-field"
                disabled={!helperFormData.department}
              >
                <option value="">Select position...</option>
                {(POSITIONS[helperFormData.department] || []).map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <textarea
            value={helperFormData.bio}
            onChange={(e) => setHelperFormData(prev => ({ ...prev, bio: e.target.value }))}
            rows={6}
            className="input-field"
            placeholder="Tell yacht owners about your experience, qualifications, certifications, and why they should work with you..."
          />

          <div className="pt-4">
            <button
              onClick={handleSaveHelperProfile}
              disabled={helperSaving}
              className="btn-primary"
            >
              {helperSaving ? (
                <span className="flex items-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Saving...
                </span>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Helper Details
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Helper: Languages */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <Globe className="h-5 w-5 inline mr-2" />
            Languages
          </h2>
          <p className="text-navy-600 mb-4">
            Add the languages you speak to help yacht owners find you.
          </p>

          {/* Current Languages */}
          <div className="flex flex-wrap gap-2 mb-4">
            {languages.length === 0 ? (
              <p className="text-navy-400 italic">No languages added yet</p>
            ) : (
              languages.map((lang) => (
                <span
                  key={lang}
                  className="inline-flex items-center px-3 py-1.5 bg-primary-100 text-primary-800 rounded-full"
                >
                  <Globe className="h-3.5 w-3.5 mr-1.5" />
                  {lang}
                  <button
                    onClick={() => handleRemoveLanguage(lang)}
                    className="ml-2 p-0.5 hover:bg-primary-200 rounded-full"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))
            )}
          </div>

          {/* Add Language */}
          <div className="relative">
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="btn-secondary inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Language
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>

            {showLanguageDropdown && (
              <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto w-64">
                {availableLanguages
                  .filter(lang => !languages.includes(lang))
                  .map((lang) => (
                    <button
                      key={lang}
                      onClick={() => handleAddLanguage(lang)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-navy-900"
                    >
                      {lang}
                    </button>
                  ))}
                {availableLanguages.filter(lang => !languages.includes(lang)).length === 0 && (
                  <p className="px-4 py-3 text-navy-400 text-sm">All languages added</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Helper: Availability Status */}
      {isHelper && (
        <div className={`card ${!availableNow ? 'border-2 border-orange-300 bg-orange-50' : 'border-2 border-green-300 bg-green-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-lg font-display font-semibold text-navy-900 mb-2">
                {availableNow ? (
                  <ToggleRight className="h-5 w-5 inline mr-2 text-green-600" />
                ) : (
                  <ToggleLeft className="h-5 w-5 inline mr-2 text-orange-600" />
                )}
                Availability Status
              </h2>
              {availableNow ? (
                <p className="text-green-700 text-sm">
                  ✅ You're <strong>available for work</strong> — yacht owners can find you in search results and send you job requests.
                </p>
              ) : (
                <div>
                  <p className="text-orange-800 font-medium mb-1">
                    ⚠️ You're currently set to <strong>offline</strong>
                  </p>
                  <p className="text-orange-700 text-sm">
                    You won't appear in search results or receive new job requests. Turn this ON when you're ready to find work!
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={handleToggleAvailability}
              className={`ml-4 flex items-center px-5 py-3 rounded-full font-semibold transition-colors ${
                availableNow
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              {availableNow ? (
                <><ToggleRight className="h-5 w-5 mr-2" />Available</>
              ) : (
                <><ToggleLeft className="h-5 w-5 mr-2" />Go Available</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Helper: Service Areas */}
      {isHelper && (
        <div className={`card ${serviceAreas.length === 0 ? 'border-2 border-red-300 bg-red-50' : ''}`}>
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <MapPin className="h-5 w-5 inline mr-2" />
            Service Areas
            {serviceAreas.length === 0 && (
              <span className="ml-2 text-sm font-normal text-red-600">(Required)</span>
            )}
          </h2>

          {serviceAreas.length === 0 ? (
            <div className="p-4 bg-red-100 border border-red-200 rounded-lg mb-4">
              <p className="text-red-800 font-medium mb-2">
                ⚠️ You won't appear in search results without service areas!
              </p>
              <p className="text-red-700 text-sm">
                Add ALL ports near your location where you can work. The more ports you add, the more job opportunities you'll see and the more visible you'll be to yacht owners looking for crew.
              </p>
            </div>
          ) : (
            <p className="text-navy-600 mb-4">
              Add more ports to increase your visibility. You'll receive notifications for new requests in these areas.
            </p>
          )}

          {/* Port Search */}
          <div className="relative mb-4">
            <input
              type="text"
              value={portSearch}
              onChange={(e) => setPortSearch(e.target.value)}
              className="input-field pl-10"
              placeholder="Search for a port to add..."
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

            {searchingPorts && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="spinner w-5 h-5" />
              </div>
            )}

            {portResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                {portResults.map((port) => (
                  <button
                    key={port.id}
                    onClick={() => handleAddPort(port)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-navy-900">{port.name}</p>
                      <p className="text-sm text-navy-500">{port.country}</p>
                    </div>
                    <Plus className="h-5 w-5 text-primary-600" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Current Service Areas */}
          <div className="flex flex-wrap gap-2">
            {serviceAreas.length === 0 ? (
              <p className="text-red-500 font-medium">No service areas added yet — search above to add your first port!</p>
            ) : (
              serviceAreas.map((area) => (
                <span
                  key={area.id}
                  className="inline-flex items-center px-3 py-1.5 bg-primary-100 text-primary-800 rounded-full"
                >
                  <MapPin className="h-3.5 w-3.5 mr-1.5" />
                  {area.port_name}
                  <button
                    onClick={() => handleRemovePort(area.id)}
                    className="ml-2 p-0.5 hover:bg-primary-200 rounded-full"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Helper: Documents & References */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <FileText className="h-5 w-5 inline mr-2" />
            Documents & References
          </h2>
          <p className="text-navy-600 mb-6">
            Upload your CV and reference letters to build trust with yacht owners.
          </p>

          {/* Upload Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {documentTypes.map((docType) => (
              <label key={docType.value} className="relative cursor-pointer">
                <input
                  type="file"
                  className="sr-only"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  data-doctype={docType.value}
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors">
                  <Upload className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-sm font-medium text-navy-700">{docType.label}</span>
                </div>
              </label>
            ))}
          </div>

          {uploading && (
            <div className="flex items-center justify-center py-4 text-navy-600">
              <span className="spinner h-5 w-5 mr-2" />
              Uploading...
            </div>
          )}

          {/* Documents List */}
          {documents.length === 0 ? (
            <div className="text-center py-8 text-navy-400">
              <File className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No documents uploaded yet</p>
              <p className="text-sm mt-1">Upload your CV and reference letters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center min-w-0">
                    <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-sm font-medium text-navy-900 truncate">
                        {doc.file_name}
                      </p>
                      <div className="flex items-center text-xs text-navy-500 mt-0.5">
                        <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 rounded mr-2">
                          {getDocTypeLabel(doc.document_type)}
                        </span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span className="mx-1">&bull;</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete document"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-navy-500">
            Accepted formats: PDF, JPEG, PNG, DOC, DOCX. Maximum file size: 10MB.
          </p>
        </div>
      )}

      {/* VISA/CERTS: Visa and Certificate sections for helpers */}
      {isHelper && (
        <>
          <VisaSection userId={user?.id} />
          <CertificateSection userId={user?.id} />
        </>
      )}

      {/* Security */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Lock className="h-5 w-5 inline mr-2" />
          Security
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-navy-900">Password</p>
              <p className="text-sm text-navy-500">Keep your account secure</p>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="btn-secondary py-2"
            >
              Change Password
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-navy-900">Two-Factor Authentication</p>
              <p className="text-sm text-navy-500">Add an extra layer of security</p>
            </div>
            <button className="btn-secondary py-2">
              Enable
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Bell className="h-5 w-5 inline mr-2" />
          Notification Preferences
        </h3>

        <div className="space-y-3">
          {[
            { label: 'New request notifications', description: 'Get notified about new requests in your area' },
            { label: 'Message notifications', description: 'Receive alerts when you get new messages' },
            { label: 'Service updates', description: 'Updates about your confirmed services' },
            { label: 'Email digest', description: 'Weekly summary of activity' },
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3">
              <div>
                <p className="font-medium text-navy-900">{item.label}</p>
                <p className="text-sm text-navy-500">{item.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Ratings & Reviews */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-6">
          <Star className="h-5 w-5 inline mr-2" />
          Ratings & Reviews
        </h3>

        {/* Reviews I've Received */}
        <div className="mb-8">
          <h4 className="text-md font-semibold text-navy-800 mb-3">Reviews I've Received</h4>

          {loadingReceivedReviews ? (
            <div className="flex items-center justify-center py-6">
              <div className="spinner w-6 h-6" />
            </div>
          ) : receivedReviews.length === 0 ? (
            <div className="text-center py-6 text-navy-400">
              <p>No reviews received yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {receivedReviews.map((review, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-navy-900">
                      {review.reviewer_first_name} {review.reviewer_last_name}
                    </p>
                    <p className="text-xs text-navy-400">
                      {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center mt-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  {review.comment && (
                    <p className="text-sm text-navy-600 mt-2">"{review.comment}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <hr className="border-gray-200 mb-8" />

        {/* Reviews I've Given */}
        <div>
          <h4 className="text-md font-semibold text-navy-800 mb-3">Reviews I've Given</h4>

          {loadingSentReviews ? (
            <div className="flex items-center justify-center py-6">
              <div className="spinner w-6 h-6" />
            </div>
          ) : sentReviews.length === 0 ? (
            <div className="text-center py-6 text-navy-400">
              <p>You haven't written any reviews yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sentReviews.map((review) => (
                <div
                  key={review.id}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-navy-900">
                          {review.first_name} {review.last_name}
                        </p>
                        <p className="text-xs text-navy-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center mt-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      {review.comment && (
                        <p className="text-sm text-navy-600 mt-2">"{review.comment}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                      <button
                        onClick={() => handleEditReview(review)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Edit review"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete review"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-semibold text-navy-900">
                Change Password
              </h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ current: '', new: '', confirm: '' });
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-navy-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.current}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                  className="input-field"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                  className="input-field"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                  className="input-field"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ current: '', new: '', confirm: '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordData.current || !passwordData.new || !passwordData.confirm}
                className="btn-primary"
              >
                {changingPassword ? (
                  <span className="flex items-center">
                    <span className="spinner h-4 w-4 mr-2" />
                    Changing...
                  </span>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Review Modal */}
      {editingReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-semibold text-navy-900">
                Edit Review
              </h3>
              <button
                onClick={() => setEditingReview(null)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-navy-500" />
              </button>
            </div>

            <p className="text-sm text-navy-600 mb-4">
              Review for <span className="font-medium">{editingReview.first_name} {editingReview.last_name}</span>
            </p>

            {/* Star Picker */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-navy-700 mb-2">Rating</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setEditReviewData(prev => ({ ...prev, rating: s }))}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star
                      className={`h-7 w-7 ${s <= editReviewData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">Comment</label>
              <textarea
                value={editReviewData.comment}
                onChange={(e) => setEditReviewData(prev => ({ ...prev, comment: e.target.value }))}
                rows={4}
                className="input-field"
                placeholder="Update your review..."
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditingReview(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReview}
                disabled={editReviewData.rating === 0}
                className="btn-primary"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Status */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Shield className="h-5 w-5 inline mr-2" />
          Verification Status
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <Mail className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-navy-900">Email Verified</p>
                <p className="text-sm text-navy-500">{user?.email}</p>
              </div>
            </div>
            <span className="badge badge-verified">Verified</span>
          </div>

          <div className="flex items-center justify-between p-3">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                <Phone className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-navy-900">Phone Number</p>
                <p className="text-sm text-navy-500">{user?.phone || 'Not added'}</p>
              </div>
            </div>
            {user?.phone ? (
              <span className="badge badge-verified">Verified</span>
            ) : (
              <button className="text-primary-600 text-sm font-medium">Add</button>
            )}
          </div>

          <div className="flex items-center justify-between p-3">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                <User className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-navy-900">ID Verification</p>
                <p className="text-sm text-navy-500">Government-issued ID</p>
              </div>
            </div>
            <button className="text-primary-600 text-sm font-medium">Verify</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
